<template>
  <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" role="radiogroup" aria-label="Плата за габарити">
    <button
        v-for="opt in options"
        :key="opt.value"
        type="button"
        role="radio"
        :aria-checked="selected === opt.value"
        @click="$emit('select', opt.value)"
        @keydown.enter.space.prevent="$emit('select', opt.value)"
        :class="[
          'cursor-pointer h-25 sm:h-25 p-4 rounded-2xl border bg-white text-center transition-all duration-200',
          'flex flex-col items-center justify-between select-none outline-none',
          'focus-visible:ring-2 focus-visible:ring-primary/40',
          selected === opt.value
            ? 'border-red-700 shadow-sm'
            : 'border-gray-200 hover:border-gray-400 hover:shadow'
        ]"
    >
      <!-- Иконка -->
      <div class="icon-box text-gray-800">
        <svg v-if="opt.value==='zero'" height="43" viewBox="0 0 194 102" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
              d="M82.9522 61.0556C75.4065 53.6735 71.847 49.8898 69 45C75.0816 47.0248 76.3678 46.6538 91.4232 60.1111C92.126 62.4563 92.2558 63.5133 91.4232 64.3611C84.0259 73.2805 80.6005 75.9101 74.9795 79C79.2535 70.6755 82.8895 66.255 82.9522 61.0556Z"
              stroke="black" stroke-width="2"/>
          <circle cx="161" cy="68.5" r="18.75" stroke="black" stroke-width="2.5"/>
          <circle cx="161" cy="68.5" r="31.75" stroke="black" stroke-width="2.5"/>
          <circle cx="33" cy="69" r="31.75" stroke="black" stroke-width="2.5"/>
          <path
              d="M61.1845 45L57.1845 41M61.1845 45C74.016 58.1288 72.7636 67.5348 67.6845 85L107.184 85.5C118.249 82.6296 122.145 79.9183 123.684 72.5M61.1845 45L34 70.5C30.5941 71.0525 30.3921 70.086 31.1845 67.5L57.1845 41M57.1845 41C49.4299 34.9723 44.4297 32.2003 31.1845 31.2761M50.6845 24L54.6845 22.5L67.1845 26C69.5539 24.8284 69.5577 24.0267 68.6845 22.5L60.9477 20.25M50.6845 24L53.2108 18M50.6845 24L47.1845 25.306M31.1845 31.2761L17.1845 36.5C16.6387 33.874 17.2556 32.7586 19.6845 31.2761C41.0776 14.1126 52.5 5.84165 68.6845 1C63.9818 3.65443 60.2027 5.92646 57.1845 7.93873M31.1845 31.2761L47.1845 25.306M123.684 72.5L161 75M123.684 72.5L124.684 63.5M124.684 63.5L161 62M124.684 63.5L122.684 50M122.684 50L117.684 52C109.915 49.9631 99.9484 44.5312 83.6845 36M122.684 50C124.061 43.7055 125.724 41.4881 129.684 39L171.184 19.5H163.684C156.487 17.7581 152.541 17.3946 145.684 18C140.006 20.0225 136.696 22.0681 130.684 26.5M83.6845 36L110.684 37C119.885 34.5459 125.089 33.2228 130.684 26.5M83.6845 36C89.487 34.8564 94.201 32.6323 103.685 27.5M83.6845 36L59.6845 33.5L47.1845 25.306M130.684 26.5C119.994 28.9184 114.076 29.1941 103.685 27.5M54.6845 14.5C52.0806 15.7077 50.5316 16.622 46.1845 22.5C44.0233 20.0363 47.0968 15.1916 57.1845 7.93873M54.6845 14.5L53.2108 18M54.6845 14.5C57.3467 13.4675 59.749 13.1374 65.6845 13C67.8814 10.6812 68.669 9.16004 69.1845 6L67.1845 4.5L57.1845 7.93873M53.2108 18L60.9477 20.25M60.9477 20.25C69.9666 17.4764 82.6845 19.5 82.6845 19.5C82.6845 19.5 96.4717 23.0618 103.685 27.5"
              stroke="black" stroke-width="2"/>
          <circle cx="160.5" cy="68.5" r="6.25" stroke="black" stroke-width="2.5"/>
          <circle cx="32.5" cy="68.5" r="6.25" stroke="black" stroke-width="2.5"/>
          <circle cx="161" cy="69" r="1.75" stroke="black" stroke-width="2.5"/>
          <circle cx="33" cy="69" r="18.75" stroke="black" stroke-width="2.5"/>
        </svg>
        <div v-else-if="opt.value==='one'" class="svg-raw" v-html="crossoverIcon"></div>
        <div v-else class="svg-raw" v-html="pickupIcon"></div>
      </div>

      <!-- Подписи -->
      <div class="leading-tight">
        <div class="text-[13px] sm:text-sm font-medium text-gray-900">{{ opt.label }}</div>
        <div v-if="opt.badge" class="text-[12px] sm:text-[13px] font-semibold text-primary">{{ opt.badge }}</div>
      </div>
    </button>
  </div>
</template>

<script setup>
import {computed, defineEmits, defineProps} from 'vue'
import {crossover, notStandard} from '@/helpers/helper-svg'

defineProps({selected: {type: String, default: ''}})
defineEmits(['select'])

const crossoverIcon = crossover
const pickupIcon = notStandard

const options = computed(() => ([
  {value: 'zero', label: 'Motorcycle', badge: ''},
  {value: 'one', label: 'Crossover', badge: '+100$'},
  {value: 'two', label: 'Pickup', badge: '×1.5'},
  {value: 'three', label: 'Pickup', badge: '×2'}
]))
</script>

<style scoped>
/* Единый вид линий и масштаб */
.icon-box {
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.svg-std {
  height: 100%;
  width: auto;
  display: block;
}

.stroke {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
}

/* Приводим v-html SVG (crossover/pickup) к одному размеру и стилю */
.svg-raw :deep(svg) {
  height: 100%;
  width: 100%;
  display: block;
}

.svg-raw :deep(path),
.svg-raw :deep(circle),
.svg-raw :deep(rect),
.svg-raw :deep(line),
.svg-raw :deep(polyline),
.svg-raw :deep(polygon) {
  stroke: currentColor !important;
  fill: none !important;
  stroke-width: 2 !important;
}
</style>